<script setup lang="ts">
import { computed } from 'vue';

import type { VRouterTab } from '@components/tabs/VRouterTabs/types';
import VRouterTabs from '@components/tabs/VRouterTabs/VRouterTabs.vue';
import {
  ADMIN_GENERAL_SETTINGS_AFFILIATE_BANNER_PERMISSIONS,
  ADMIN_GENERAL_SETTINGS_AFFILIATE_BANNER_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_AFFILIATE_BANNER,
} from '@modules/general-settings/submodules/affiliate-banner/constants';
import {
  ADMIN_GENERAL_SETTINGS_AUTO_APPROVE_PERMISSIONS,
  ADMIN_GENERAL_SETTINGS_AUTO_APPROVE_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_AUTO_APPROVE,
} from '@modules/general-settings/submodules/auto-approve/constants';
import {
  ADMIN_GENERAL_SETTINGS_BOOKKEEPING_LIST_ROUTE_NAME,
  ADMIN_GENERAL_SETTINGS_BOOKKEEPING_PERMISSIONS,
  TRANSLATION_GROUP_GENERAL_SETTINGS_BOOKKEEPING,
} from '@modules/general-settings/submodules/bookkeeping/constants';
import {
  ADMIN_GENERAL_SETTINGS_CLICK_DOMAINS_LIST_ROUTE_NAME,
  ADMIN_GENERAL_SETTINGS_CLICK_DOMAINS_PERMISSIONS,
  TRANSLATION_GROUP_GENERAL_SETTINGS_CLICK_DOMAINS,
} from '@modules/general-settings/submodules/click-domains/constants';
import {
  ADMIN_GENERAL_SETTINGS_EMAILS_PERMISSIONS,
  ADMIN_GENERAL_SETTINGS_EMAILS_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_EMAILS,
} from '@modules/general-settings/submodules/emails/constants';
import {
  ADMIN_GENERAL_SETTINGS_FULL_STATS_ACCESS_PERMISSIONS,
  ADMIN_GENERAL_SETTINGS_FULL_STATS_ACCESS_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_FULL_STATS_ACCESS,
} from '@modules/general-settings/submodules/full-stats-access/constants';
import {
  ADMIN_GENERAL_SETTINGS_INFORMATION_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_INFORMATION,
} from '@modules/general-settings/submodules/information/constants';
import {
  ADMIN_GENERAL_SETTINGS_NOTIFICATIONS_PERMISSIONS,
  ADMIN_GENERAL_SETTINGS_NOTIFICATIONS_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS,
} from '@modules/general-settings/submodules/notifications/constants';
import {
  ADMIN_GENERAL_SETTINGS_PROJECT_PERMISSIONS,
  ADMIN_GENERAL_SETTINGS_PROJECT_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT,
} from '@modules/general-settings/submodules/project-settings/constants';
import {
  ADMIN_GENERAL_SETTINGS_REGISTRATION_FIELDS_PERMISSIONS,
  ADMIN_GENERAL_SETTINGS_REGISTRATION_FIELDS_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_REGISTRATION_FIELDS,
} from '@modules/general-settings/submodules/registration-fields/constants';
import {
  ADMIN_GENERAL_SETTINGS_TABLE_COLUMNS_SETTINGS_MAIN_ROUTE_NAME,
  ADMIN_GENERAL_SETTINGS_TABLE_COLUMNS_SETTINGS_PERMISSIONS,
  TRANSLATION_GROUP_GENERAL_SETTINGS_TABLE_COLUMNS_SETTINGS,
} from '@modules/general-settings/submodules/table-columns-settings/constants';
import {
  ADMIN_GENERAL_SETTINGS_UPLOAD_LOGO_PERMISSIONS,
  ADMIN_GENERAL_SETTINGS_UPLOAD_LOGO_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_UPLOAD_LOGO,
} from '@modules/general-settings/submodules/upload-logo/constants';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import useStaticPageConfig from '@modules/static-pages/composables/useStaticPageConfig';

const { getTranslation } = useI18n();
const { hasPermission } = usePermissionsStore();
const { getStaticPagesPermissions } = useStaticPageConfig();

const tabItems = computed<VRouterTab[]>(() => [
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_PROJECT_ROUTE_NAME,
    },
    visible: hasPermission(ADMIN_GENERAL_SETTINGS_PROJECT_PERMISSIONS.PROJECTSETTINGS_EDIT).value,
  },
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AUTO_APPROVE, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_AUTO_APPROVE_ROUTE_NAME,
    },
    visible: hasPermission(
      ADMIN_GENERAL_SETTINGS_AUTO_APPROVE_PERMISSIONS.AUTOAPPROVELOCALES_SHOW,
    ).value,
  },
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_INFORMATION, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_INFORMATION_ROUTE_NAME,
    },
    visible: hasPermission(getStaticPagesPermissions()).value,
  },
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_EMAILS, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_EMAILS_ROUTE_NAME,
    },
    visible: hasPermission(ADMIN_GENERAL_SETTINGS_EMAILS_PERMISSIONS.MAILERS_MANAGER_EDIT).value,
  },
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_BOOKKEEPING, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_BOOKKEEPING_LIST_ROUTE_NAME,
    },
    visible: hasPermission(ADMIN_GENERAL_SETTINGS_BOOKKEEPING_PERMISSIONS.HISTORYOFLIMITCHANGES_CREATE).value,
  },
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_TABLE_COLUMNS_SETTINGS, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_TABLE_COLUMNS_SETTINGS_MAIN_ROUTE_NAME,
    },
    visible: hasPermission(
      ADMIN_GENERAL_SETTINGS_TABLE_COLUMNS_SETTINGS_PERMISSIONS.SHOW,
    ).value,
  },
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_FULL_STATS_ACCESS, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_FULL_STATS_ACCESS_ROUTE_NAME,
    },
    visible: hasPermission(
      ADMIN_GENERAL_SETTINGS_FULL_STATS_ACCESS_PERMISSIONS.PROJECTSETTINGS_ADMINS_WITH_FULL_STATS_ACCESS,
    ).value,
  },
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_REGISTRATION_FIELDS, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_REGISTRATION_FIELDS_ROUTE_NAME,
    },
    visible: hasPermission(
      ADMIN_GENERAL_SETTINGS_REGISTRATION_FIELDS_PERMISSIONS.PROJECTSETTINGS_REQUIRED_FIELDS_IN_PROFILE_UPDATE,
    ).value,
  },
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_UPLOAD_LOGO, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_UPLOAD_LOGO_ROUTE_NAME,
    },
    visible: hasPermission(
      ADMIN_GENERAL_SETTINGS_UPLOAD_LOGO_PERMISSIONS.PROJECTSETTINGS_EDIT,
    ).value,
  },
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_NOTIFICATIONS_ROUTE_NAME,
    },
    visible: hasPermission(ADMIN_GENERAL_SETTINGS_NOTIFICATIONS_PERMISSIONS.NOTIFICATIONS_SHOW).value,
  },
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AFFILIATE_BANNER, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_AFFILIATE_BANNER_ROUTE_NAME,
    },
    visible: hasPermission(
      ADMIN_GENERAL_SETTINGS_AFFILIATE_BANNER_PERMISSIONS.BANNERSETTINGS_SHOW,
    ).value,
  },
  {
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_CLICK_DOMAINS, 'TAB_NAME').value,
    route: {
      name: ADMIN_GENERAL_SETTINGS_CLICK_DOMAINS_LIST_ROUTE_NAME,
    },
    visible: hasPermission(
      ADMIN_GENERAL_SETTINGS_CLICK_DOMAINS_PERMISSIONS.CLICKDOMAINS_SHOW,
    ).value,
  },
]);
</script>

<template>
  <VRouterTabs v-data-qa-auto-id="'AdminGeneralSettingsTabs'" :items="tabItems" />
</template>
